import { createGesture } from '@ionic/react'
import { useEffect, useRef } from 'react'

/**
 * Edge-swipe → back gesture, intentionally WITHOUT the native slide animation.
 *
 * Why manual: v2's keep-alive shell (TabLayout) keeps the real tab roots
 * outside the outlet, and Back must stay inside the tab (ADR-0008). Ionic's
 * native swipe-back would bypass that, so the tab shell turns it off while
 * mounted (useNativeSwipeBackOff, ADR-0009) and this hook owns the edge
 * swipe there.
 * Why no animation: an animated version was removed on purpose — hand-mutating
 * page transforms risks real navigation bugs for a purely cosmetic effect.
 */

const EDGE_WIDTH = 50 // px from the left edge where the gesture may start
const TRIGGER_DISTANCE = 100
const FLICK_DISTANCE = 40
const FLICK_VELOCITY = 0.4 // px/ms

/** Exported for tests. */
export const shouldTriggerBack = (deltaX: number, velocityX: number): boolean =>
  deltaX > TRIGGER_DISTANCE ||
  (deltaX > FLICK_DISTANCE && velocityX > FLICK_VELOCITY)

export const useSwipeBack = <T extends HTMLElement>(onBack: () => void) => {
  const ref = useRef<T | null>(null)
  const onBackRef = useRef(onBack)
  onBackRef.current = onBack

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const gesture = createGesture({
      el,
      gestureName: 'swipe-back',
      gesturePriority: 40, // above content scroll
      threshold: 10,
      direction: 'x',
      canStart: (detail) => detail.startX <= EDGE_WIDTH,
      onEnd: (detail) => {
        if (shouldTriggerBack(detail.deltaX, detail.velocityX)) {
          onBackRef.current()
        }
      },
    })
    gesture.enable(true)
    return () => gesture.destroy()
  }, [])

  return ref
}
